import sitecoreScreens, {
  sitecoreResponsiveGallery,
} from "../../sitecoreMedia";

const sitecore = {
  id: "sitecore",
  slug: "sitecore",
  category: "Applications",
  navTitle: "Modernizing the Enterprise Website with Sitecore",
  title: "Modernizing the Enterprise Website with Sitecore",
  introduction: [
    "Co-operators needed to modernize its public website while giving marketing and content teams greater control over publishing. I led the experience and design-system integration for the Sitecore implementation, using Oak to create a responsive, accessible, and scalable foundation for the organization’s web presence.",
  ],
  challenge: [
    {
      title: "A fragmented web experience",
      paragraphs: [
        "The existing site lacked consistent patterns and clear navigation, making content harder to find and use.",
      ],
    },
    {
      title: "Publishing bottlenecks",
      paragraphs: [
        "Marketing teams relied on developers for routine updates, slowing publishing and limiting their ability to respond quickly.",
      ],
    },
    {
      title: "Complex content requirements",
      paragraphs: [
        "The platform needed to support regional variation, multilingual content, and different business needs without fragmenting the experience.",
      ],
    },
    {
      title: "Accessibility gaps",
      paragraphs: [
        "The new site needed to meet accessibility requirements across templates, content, and responsive behaviour.",
      ],
    },
  ],
  process: [
    {
      title: "Defining the platform approach",
      paragraphs: [
        "I worked with marketing, design, and technology teams to align customer, content, and implementation needs.",
      ],
      bullets: [
        "Established Oak as the design foundation for the new site.",
        "Defined priorities for navigation, responsive behaviour, accessibility, and authoring.",
        "Created a phased roadmap for moving content and functionality from the legacy platform.",
      ],
    },
    {
      title: "Designing for reuse",
      paragraphs: [
        "The content model needed to give teams flexibility without allowing every area to create its own version of the website.",
      ],
      bullets: [
        "Structured the experience around modular content and reusable templates.",
        "Supported regional and multilingual variations.",
        "Created patterns that could serve different products and business areas.",
        "Enabled marketing teams to build and update pages without compromising brand or accessibility standards.",
      ],
    },
    {
      title: "Bringing Oak into Sitecore",
      paragraphs: [
        "I worked with design and development teams to translate Oak into practical templates and authoring patterns.",
      ],
      bullets: [
        "Applied shared standards for layout, typography, interaction, and accessibility.",
        "Created responsive templates that worked across devices.",
        "Built design constraints into the CMS so content authors could work independently while maintaining consistency.",
        "Validated the templates through usability and accessibility testing.",
      ],
    },
    {
      title: "Preparing teams to own the platform",
      paragraphs: [
        "The project was not complete when the website launched. Internal teams needed to be able to manage it confidently.",
      ],
      bullets: [
        "Created documentation and delivered workshops for marketing and content teams.",
        "Established guidance for accessible, brand-aligned content.",
        "Coordinated a phased rollout so issues could be identified and addressed before full launch.",
      ],
    },
  ],
  outcomes: [
    {
      title: "A more unified web experience",
      description:
        "The new site improved consistency in navigation, branding, interaction patterns, and responsive behaviour.",
    },
    {
      title: "Greater marketing autonomy",
      description:
        "Reusable templates and structured authoring tools reduced reliance on developers for routine updates.",
    },
    {
      title: "Improved usability",
      description:
        "Customer surveys showed a 50% improvement in ease of navigation and content findability.",
    },
    {
      title: "Accessibility by default",
      description:
        "Oak standards and accessible templates helped the site achieve WCAG 2.1 compliance and reduced the risk of new issues being introduced during routine content updates.",
    },
    {
      title: "A scalable publishing foundation",
      description:
        "The modular content model supported regional, multilingual, and business-specific needs without requiring separate experiences for each area.",
    },
  ],
  reflection: [
    "The Sitecore work reinforced that a CMS transformation is not mainly a technology project. The difficult part is aligning customer experience, content governance, design standards, authoring needs, and technical constraints so the platform works for both customers and the teams maintaining it.",
    "Oak provided the visual and accessibility foundation. The content model gave marketing teams greater control–and it was a platform the organization could maintain and evolve without losing consistency every time something changed.",
  ],
  media: {
    blocks: [
      {
        id: "sitecore-responsive-composition",
        type: "composition",
        placement: "afterIntro",
        className: "work-page__media-block--integrated-opening",
        items: sitecoreScreens,
        aspectRatio: "3 / 2",
        label:
          "Co-operators Sitecore website template across desktop, tablet, and mobile",
        galleryLabel: "Co-operators Sitecore responsive website gallery",
        alt: "Co-operators responsive home insurance website across desktop, tablet, and mobile",
      },
      {
        id: "sitecore-responsive-system",
        type: "grid",
        placement: "afterChallenge",
        title: "One content system across every screen",
        description:
          "The same authored modules adapt from desktop to tablet and mobile while preserving content hierarchy, calls to action, navigation, and access to supporting resources.",
        layout: "default",
        options: {
          maxWidth: 1000,
          tileMin: 280,
          thumbHeight: 440,
          thumbFit: "contain",
          modalMaxWidth: 1180,
          modalMaxHeight: "min(90vh, 960px)",
        },
        items: sitecoreResponsiveGallery,
      },
    ],
  },
};

export default sitecore;
